import { useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { toast } from "sonner";
import { EnvelopeSimple, CheckCircle, XCircle, Clock, User, Funnel, ArrowClockwise } from "@phosphor-icons/react";

const DEFAULT_FILTERS = { student_id: "", class_id: "", status: "all", from_date: "", to_date: "" };

export default function EmailLogs() {
  const [state, setState] = useState({ items: [], total: 0, sent: 0, failed: 0 });
  const [loading, setLoading] = useState(true);
  const [students, setStudents] = useState([]);
  const [classes, setClasses] = useState([]);
  const [filters, setFilters] = useState(DEFAULT_FILTERS);

  useEffect(() => {
    // Reference data for filters
    Promise.all([
      api.get("/students").then(r => setStudents(r.data)).catch(() => {}),
      api.get("/classes").then(r => setClasses(r.data)).catch(() => {}),
    ]);
  }, []);

  const load = () => {
    setLoading(true);
    const params = new URLSearchParams({ limit: "200" });
    if (filters.student_id) params.set("student_id", filters.student_id);
    if (filters.class_id) params.set("class_id", filters.class_id);
    if (filters.status && filters.status !== "all") params.set("status", filters.status);
    if (filters.from_date) params.set("from_date", filters.from_date);
    if (filters.to_date) params.set("to_date", filters.to_date);
    api.get(`/bulletin-emails?${params.toString()}`)
      .then(r => setState(r.data))
      .catch(e => toast.error(formatApiError(e)))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filters]);

  const reset = () => setFilters(DEFAULT_FILTERS);

  const hasFilters = useMemo(() =>
    JSON.stringify(filters) !== JSON.stringify(DEFAULT_FILTERS), [filters]);

  return (
    <div className="p-6 lg:p-10 max-w-6xl mx-auto">
      <header className="mb-6">
        <div className="text-xs uppercase tracking-widest text-secondary font-semibold">Suivi</div>
        <h1 className="mt-2 font-heading text-3xl font-bold tracking-tight flex items-center gap-3">
          <EnvelopeSimple size={32} weight="duotone" className="text-primary" />
          Historique des envois de bulletins
        </h1>
        <p className="text-sm text-muted-foreground mt-1">Trace complète des bulletins annuels envoyés par email aux parents.</p>
      </header>

      {/* Filters */}
      <Card className="mb-4 p-4" data-testid="email-logs-filters">
        <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-primary font-semibold mb-3">
          <Funnel size={12} weight="duotone" /> Filtres
          {hasFilters && (
            <Button size="sm" variant="ghost" onClick={reset} className="ml-auto h-6 text-xs" data-testid="btn-reset-filters">
              <ArrowClockwise size={12} className="mr-1" /> Réinitialiser
            </Button>
          )}
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
          <div>
            <Label className="text-[10px] uppercase tracking-wider text-muted-foreground">Du</Label>
            <Input type="date" value={filters.from_date} onChange={e => setFilters({ ...filters, from_date: e.target.value })} data-testid="filter-from-date" />
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-wider text-muted-foreground">Au</Label>
            <Input type="date" value={filters.to_date} onChange={e => setFilters({ ...filters, to_date: e.target.value })} data-testid="filter-to-date" />
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-wider text-muted-foreground">Classe</Label>
            <Select value={filters.class_id || "__all__"} onValueChange={v => setFilters({ ...filters, class_id: v === "__all__" ? "" : v })}>
              <SelectTrigger data-testid="filter-class"><SelectValue placeholder="Toutes" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__all__">Toutes les classes</SelectItem>
                {classes.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-wider text-muted-foreground">Élève</Label>
            <Select value={filters.student_id || "__all__"} onValueChange={v => setFilters({ ...filters, student_id: v === "__all__" ? "" : v })}>
              <SelectTrigger data-testid="filter-student"><SelectValue placeholder="Tous" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__all__">Tous les élèves</SelectItem>
                {students.slice(0, 300).map(s => (
                  <SelectItem key={s.id} value={s.id}>{s.last_name} {s.first_name} · {s.matricule}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-wider text-muted-foreground">Statut</Label>
            <Select value={filters.status} onValueChange={v => setFilters({ ...filters, status: v })}>
              <SelectTrigger data-testid="filter-status"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tous</SelectItem>
                <SelectItem value="sent">✓ Envoyés</SelectItem>
                <SelectItem value="failed">✗ Échecs</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      </Card>

      <div className="grid grid-cols-3 gap-3 mb-6">
        <Card className="p-4" data-testid="kpi-total">
          <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">Total envois</div>
          <div className="font-heading text-3xl font-bold mt-1 tabular-nums">{state.total}</div>
        </Card>
        <Card className="p-4 border-emerald-500/40" data-testid="kpi-sent">
          <div className="text-[10px] uppercase tracking-wider text-emerald-600 font-semibold flex items-center gap-1">
            <CheckCircle size={12} weight="fill" /> Envoyés
          </div>
          <div className="font-heading text-3xl font-bold mt-1 tabular-nums text-emerald-600">{state.sent}</div>
        </Card>
        <Card className="p-4 border-destructive/40" data-testid="kpi-failed">
          <div className="text-[10px] uppercase tracking-wider text-destructive font-semibold flex items-center gap-1">
            <XCircle size={12} weight="fill" /> Échecs
          </div>
          <div className="font-heading text-3xl font-bold mt-1 tabular-nums text-destructive">{state.failed}</div>
        </Card>
      </div>

      <Card className="overflow-hidden" data-testid="email-logs-table">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-muted/40 text-[10px] uppercase tracking-wider text-muted-foreground">
              <tr>
                <th className="px-3 py-2 text-left">Date</th>
                <th className="px-3 py-2 text-left">Élève</th>
                <th className="px-3 py-2 text-left">Email parent</th>
                <th className="px-3 py-2 text-center">Langue</th>
                <th className="px-3 py-2 text-center">Statut</th>
                <th className="px-3 py-2 text-left">Envoyé par</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={6} className="px-3 py-8 text-center text-muted-foreground">Chargement…</td></tr>
              ) : state.items.length === 0 ? (
                <tr><td colSpan={6} className="px-3 py-8 text-center text-muted-foreground">
                  {hasFilters ? "Aucun envoi ne correspond à ces filtres." : "Aucun envoi enregistré pour le moment."}
                </td></tr>
              ) : state.items.map((it, i) => (
                <tr key={it.id} className={`border-t border-border ${i % 2 ? "bg-muted/20" : ""}`} data-testid={`email-log-${it.id}`}>
                  <td className="px-3 py-2 text-xs tabular-nums whitespace-nowrap">
                    <Clock size={10} weight="duotone" className="inline mr-1 text-muted-foreground" />
                    {(() => {
                      try {
                        return new Date(it.sent_at).toLocaleString(undefined, { dateStyle: "short", timeStyle: "short" });
                      } catch (e) {
                        return new Date(it.sent_at).toISOString().slice(0, 16).replace("T", " ");
                      }
                    })()}
                  </td>
                  <td className="px-3 py-2">
                    <div className="font-medium">{it.student_name}</div>
                    <div className="text-[10px] text-muted-foreground font-mono">{it.student_matricule}</div>
                  </td>
                  <td className="px-3 py-2 text-xs font-mono">{it.recipient_email}</td>
                  <td className="px-3 py-2 text-center">
                    <Badge variant="outline" className="text-[10px] uppercase">{it.lang}</Badge>
                  </td>
                  <td className="px-3 py-2 text-center">
                    {it.status === "sent" ? (
                      <Badge className="bg-emerald-100 text-emerald-700 border-emerald-300">
                        <CheckCircle size={10} weight="fill" className="mr-1" /> Envoyé
                      </Badge>
                    ) : (
                      <Badge variant="destructive" className="text-[10px]">
                        <XCircle size={10} weight="fill" className="mr-1" /> Échec
                      </Badge>
                    )}
                  </td>
                  <td className="px-3 py-2 text-xs">
                    <User size={10} weight="duotone" className="inline mr-1 text-muted-foreground" />
                    {it.sent_by_name}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}
